import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Cartao from '@/components/ui/Cartao';
import Chip from '@/components/ui/Chip';
import Botao from '@/components/ui/Botao';
import { formatarDinheiro } from '@/pages/gerenciar/utils';
import type { EventoPlataforma, PorOrigem, Repasse } from '../types';
import { formatarDataBR, formatarPct, rotuloForma, rotuloOnde, rotuloOrigem } from '../utils';
import ModalDesfazerRepasse from './ModalDesfazerRepasse';

interface Props {
  evento: EventoPlataforma;
  aoRegistrar: (evento: EventoPlataforma) => void;
  aoAtualizar: () => void;
}

function ChipSituacao({ evento }: { evento: EventoPlataforma }) {
  const saldo = Number(evento.saldo_centavos) || 0;
  if (evento.situacao === 'repassar') {
    return <Chip tipo="warning">Repassar {formatarDinheiro(saldo)}</Chip>;
  }
  if (evento.situacao === 'cobrar') {
    return <Chip tipo="danger">Cobrar {formatarDinheiro(Math.abs(saldo))}</Chip>;
  }
  return <Chip tipo="success">Em dia</Chip>;
}

function LinhaNumero({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="min-w-0 rounded-2xl bg-background-50 px-3 py-2.5">
      <p className="text-xs text-foreground-500">{rotulo}</p>
      <p className="mt-0.5 truncate text-base font-semibold tabular-nums text-foreground-950" title={valor}>
        {valor}
      </p>
    </div>
  );
}

function LinhaConta({
  rotulo,
  valorCentavos,
  sinal,
}: {
  rotulo: string;
  valorCentavos: number;
  sinal: '+' | '−';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="min-w-0 text-sm text-foreground-700">{rotulo}</span>
      <span className="shrink-0 whitespace-nowrap text-sm tabular-nums text-foreground-900">
        {sinal === '−' ? '− ' : '+ '}
        {formatarDinheiro(valorCentavos)}
      </span>
    </div>
  );
}

function LinhaOrigem({ item }: { item: PorOrigem }) {
  return (
    <div className="rounded-2xl border border-secondary-100 bg-background-50 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="min-w-0 text-sm font-medium text-foreground-950">
          {rotuloOrigem(item.origem)}
        </span>
        <span className="shrink-0 whitespace-nowrap text-xs text-foreground-500">
          {rotuloOnde(item.onde)}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs sm:grid-cols-5">
        <span className="text-foreground-600">
          Qtd <strong className="font-semibold text-foreground-900">{item.quantidade}</strong>
        </span>
        <span className="text-foreground-600">
          Bruto <strong className="font-semibold text-foreground-900">{formatarDinheiro(item.bruto_centavos)}</strong>
        </span>
        <span className="text-foreground-600">
          Mercado Pago <strong className="font-semibold text-foreground-900">{formatarDinheiro(item.mercado_pago_centavos)}</strong>
        </span>
        <span className="text-foreground-600">
          Se Liga <strong className="font-semibold text-foreground-900">{formatarDinheiro(item.seliga_centavos)}</strong>
        </span>
        <span className="text-foreground-600">
          Organizador <strong className="font-semibold text-foreground-900">{formatarDinheiro(item.organizador_centavos)}</strong>
        </span>
      </div>
    </div>
  );
}

function LinhaRepasse({
  repasse,
  aoDesfazer,
}: {
  repasse: Repasse;
  aoDesfazer: (r: Repasse) => void;
}) {
  const titulo =
    repasse.sentido === 'para_organizador' ? 'Se Liga → organizador' : 'Organizador → Se Liga';
  return (
    <div
      className={`rounded-2xl border border-secondary-100 bg-background-50 p-3 ${
        repasse.desfeito ? 'opacity-60' : ''
      }`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span
          className={`min-w-0 text-sm font-medium text-foreground-950 ${
            repasse.desfeito ? 'line-through' : ''
          }`}
        >
          {formatarDataBR(repasse.pago_em) || formatarDataBR(repasse.registrado_em)} · {titulo} ·{' '}
          {formatarDinheiro(repasse.valor_centavos)}
        </span>
        {!repasse.desfeito && (
          <button
            type="button"
            onClick={() => aoDesfazer(repasse)}
            className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg px-2 py-1 text-xs font-semibold text-danger-600 transition-colors hover:bg-danger-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-go-back-line" />
            </span>
            Desfazer
          </button>
        )}
      </div>
      <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-foreground-600">
        {repasse.forma && <span>{rotuloForma(repasse.forma)}</span>}
        {repasse.destino && <span>para {repasse.destino}</span>}
        {repasse.registrado_por && <span>por {repasse.registrado_por}</span>}
      </p>
      {repasse.observacao && (
        <p className="mt-1 text-xs text-foreground-500">{repasse.observacao}</p>
      )}
      {repasse.desfeito && (
        <p className="mt-1 text-xs font-medium text-danger-600">
          desfeito por {repasse.desfeito_por ?? '—'}: {repasse.desfeito_motivo ?? '—'}
        </p>
      )}
    </div>
  );
}

export default function CartaoEvento({ evento, aoRegistrar, aoAtualizar }: Props) {
  const [detalhesAberto, setDetalhesAberto] = useState(false);
  const [repasses, setRepasses] = useState<Repasse[] | null>(null);
  const [carregandoRepasses, setCarregandoRepasses] = useState(false);
  const [erroRepasses, setErroRepasses] = useState<string | null>(null);
  const [desfazerAlvo, setDesfazerAlvo] = useState<Repasse | null>(null);

  const saldo = Number(evento.saldo_centavos) || 0;

  const carregarRepasses = useCallback(async () => {
    setCarregandoRepasses(true);
    setErroRepasses(null);
    try {
      const { data, error } = await supabase.rpc('plataforma_repasses', {
        p_evento: evento.id,
      });
      if (error) throw error;
      setRepasses((data as Repasse[]) || []);
    } catch (e) {
      setErroRepasses(mensagemAmigavel(e));
    } finally {
      setCarregandoRepasses(false);
    }
  }, [evento.id]);

  function alternarDetalhes() {
    const proximo = !detalhesAberto;
    setDetalhesAberto(proximo);
    if (proximo) void carregarRepasses();
  }

  const pct = formatarPct(evento.taxa_efetiva_pct);

  const ondeLinhas: { rotulo: string; valor: number }[] = [
    { rotulo: 'Na conta do Se Liga', valor: Number(evento.na_conta_seliga_centavos) || 0 },
    { rotulo: 'Direto na conta do organizador', valor: Number(evento.direto_organizador_centavos) || 0 },
    { rotulo: 'Com a equipe (dinheiro / lançado à mão)', valor: Number(evento.fora_centavos) || 0 },
  ].filter((l) => l.valor > 0);

  const conta = {
    deveAoOrganizador: Number(evento.deve_ao_organizador_centavos) || 0,
    organizadorDeve: Number(evento.organizador_deve_centavos) || 0,
    repassado: Number(evento.repassado_centavos) || 0,
    recebido: Number(evento.recebido_do_organizador_centavos) || 0,
  };
  const temConta =
    conta.deveAoOrganizador > 0 ||
    conta.organizadorDeve > 0 ||
    conta.repassado > 0 ||
    conta.recebido > 0;

  const fraseSaldo =
    saldo > 0
      ? `O Se Liga deve repassar ${formatarDinheiro(saldo)} ao organizador.`
      : saldo < 0
        ? `O organizador deve ${formatarDinheiro(Math.abs(saldo))} ao Se Liga.`
        : 'Tudo acertado.';

  return (
    <Cartao className="min-w-0 p-4 md:p-5">
      {/* Cabeçalho */}
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-4">
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-base font-bold text-foreground-950 md:text-lg">
            {evento.nome}
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500 md:text-sm">
            {[evento.organizador, evento.cidade, formatarDataBR(evento.inicio_em)]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="shrink-0">
          <ChipSituacao evento={evento} />
        </div>
      </div>

      {/* Recebe em */}
      {(evento.recebe_em || evento.conta_mp_conectada) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {evento.recebe_em && (
            <span className="text-xs text-foreground-600 md:text-sm">
              Recebe em: <strong className="font-semibold text-foreground-900">{evento.recebe_em}</strong>
            </span>
          )}
          {evento.conta_mp_conectada && <Chip tipo="info">Mercado Pago conectado</Chip>}
        </div>
      )}

      {/* Três números */}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <LinhaNumero rotulo="Entrou" valor={formatarDinheiro(evento.entrou_centavos)} />
        <LinhaNumero
          rotulo={pct ? `Se Liga (${pct}%)` : 'Se Liga'}
          valor={formatarDinheiro(evento.seliga_centavos)}
        />
        <LinhaNumero rotulo="Mercado Pago" valor={formatarDinheiro(evento.mercado_pago_centavos)} />
      </div>

      {/* Onde está o dinheiro */}
      {ondeLinhas.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
            Onde está o dinheiro
          </p>
          <div className="mt-2 space-y-1.5">
            {ondeLinhas.map((l) => (
              <div key={l.rotulo} className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 text-sm text-foreground-700">{l.rotulo}</span>
                <span className="shrink-0 whitespace-nowrap text-sm font-medium tabular-nums text-foreground-900">
                  {formatarDinheiro(l.valor)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* A conta do repasse */}
      {temConta && (
        <div className="mt-4 rounded-2xl border border-secondary-100 bg-background-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
            A conta do repasse
          </p>
          <div className="mt-2 space-y-1.5">
            <LinhaConta
              rotulo="Parte do organizador no que está com o Se Liga"
              valorCentavos={conta.deveAoOrganizador}
              sinal="+"
            />
            <LinhaConta
              rotulo="Taxa do Se Liga no que ficou com o organizador"
              valorCentavos={conta.organizadorDeve}
              sinal="−"
            />
            <LinhaConta
              rotulo="Já repassado ao organizador"
              valorCentavos={conta.repassado}
              sinal="−"
            />
            <LinhaConta
              rotulo="Já recebido do organizador"
              valorCentavos={conta.recebido}
              sinal="+"
            />
          </div>
          <div className="mt-2 border-t border-secondary-200 pt-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-semibold text-foreground-950">Saldo</span>
              <span className="shrink-0 whitespace-nowrap text-base font-extrabold tabular-nums text-foreground-950">
                {formatarDinheiro(saldo)}
              </span>
            </div>
            <p className="mt-1 text-sm text-foreground-600">{fraseSaldo}</p>
          </div>
        </div>
      )}

      {/* Chave Pix */}
      {evento.chave_pix ? (
        <p className="mt-3 text-sm text-foreground-700">
          Repassar para:{' '}
          <strong className="font-semibold text-foreground-900">{evento.chave_pix.nome}</strong> ·{' '}
          {evento.chave_pix.mascarada}
        </p>
      ) : saldo > 0 ? (
        <p className="mt-3 text-sm font-medium text-warning-700">
          Sem chave Pix cadastrada: combine com o organizador.
        </p>
      ) : null}

      {/* Abatido por rifa */}
      {Number(evento.abatido_por_rifa_centavos) > 0 && (
        <p className="mt-2 text-sm text-info-800">
          A ação entre amigos abateu {formatarDinheiro(evento.abatido_por_rifa_centavos)} das
          inscrições (não é dinheiro novo).
        </p>
      )}

      {/* Botões */}
      <div className="mt-4 flex flex-wrap gap-2 border-t border-secondary-100 pt-4">
        <Botao
          variante="secundario"
          icone="ri-exchange-dollar-line"
          onClick={() => aoRegistrar(evento)}
        >
          Registrar repasse
        </Botao>
        <Botao variante="fantasma" onClick={alternarDetalhes}>
          <span className="flex items-center gap-1.5">
            Ver detalhes
            <span
              className={`flex h-4 w-4 items-center justify-center transition-transform ${
                detalhesAberto ? 'rotate-180' : ''
              }`}
            >
              <i className="ri-arrow-down-s-line" />
            </span>
          </span>
        </Botao>
      </div>

      {/* Detalhes */}
      {detalhesAberto && (
        <div className="mt-4 space-y-4 border-t border-secondary-100 pt-4">
          <div>
            <p className="text-sm font-semibold text-foreground-950">De onde veio o dinheiro</p>
            {evento.por_origem && evento.por_origem.length > 0 ? (
              <div className="mt-2 space-y-2">
                {evento.por_origem.map((item, i) => (
                  <LinhaOrigem key={`${item.origem}-${i}`} item={item} />
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-foreground-500">Nada por aqui ainda.</p>
            )}
          </div>

          <div>
            <p className="text-sm font-semibold text-foreground-950">Repasses</p>
            {carregandoRepasses ? (
              <div className="mt-2 space-y-2">
                {[0, 1].map((i) => (
                  <div key={i} className="h-16 animate-pulse rounded-2xl bg-secondary-100" />
                ))}
              </div>
            ) : erroRepasses ? (
              <div className="mt-2">
                <p className="text-sm text-danger-600">{erroRepasses}</p>
                <button
                  type="button"
                  onClick={() => void carregarRepasses()}
                  className="mt-2 cursor-pointer whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  Tentar de novo
                </button>
              </div>
            ) : repasses && repasses.length > 0 ? (
              <div className="mt-2 space-y-2">
                {repasses.map((r) => (
                  <LinhaRepasse key={r.id} repasse={r} aoDesfazer={setDesfazerAlvo} />
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-foreground-500">Nenhum repasse registrado ainda.</p>
            )}
          </div>
        </div>
      )}

      {desfazerAlvo && (
        <ModalDesfazerRepasse
          repasse={desfazerAlvo}
          aoFechar={() => setDesfazerAlvo(null)}
          aoFeito={(recado) => {
            setDesfazerAlvo(null);
            mostrarToast(recado, 4000, 'sucesso');
            void carregarRepasses();
            aoAtualizar();
          }}
        />
      )}
    </Cartao>
  );
}